import type { ReactNode } from 'react';
import { useUserLocationContext } from '../context/UserLocationContext';
import { useI18n } from '../i18n';
import { formatAssetDistanceLabel } from '../utils/assetGeo';
import styles from './AssetImageDistanceOverlay.module.css';

function LocationPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        d="M12 2c3.3 0 6 2.5 6 5.7 0 4.3-6 12.3-6 12.3S6 12 6 7.7C6 4.5 8.7 2 12 2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle cx="12" cy="8" r="2" fill="currentColor" />
    </svg>
  );
}

type AssetImageDistanceOverlayProps = {
  asset: Record<string, unknown>;
  children: ReactNode;
  className?: string;
  /** Extra classes for the distance pill (e.g. raise above carousel dots). */
  badgeClassName?: string;
  compact?: boolean;
  theme?: 'dark' | 'light';
};

export function AssetImageDistanceOverlay({
  asset,
  children,
  className,
  badgeClassName,
  compact = false,
  theme = 'dark',
}: AssetImageDistanceOverlayProps) {
  const { t, language } = useI18n();
  const { coordinates: viewerCoords } = useUserLocationContext();
  const distanceLabel = formatAssetDistanceLabel(asset, viewerCoords, language, '');

  const wrapClass = [styles.wrap, className].filter(Boolean).join(' ');
  const badgeClass = [
    styles.badge,
    compact ? styles.badgeCompact : '',
    theme === 'light' ? styles.lightBadge : '',
    badgeClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapClass}>
      {children}
      {distanceLabel ? (
        <span className={badgeClass} aria-label={t('list.distanceAria', { distance: distanceLabel })}>
          <LocationPinIcon className={styles.pinIcon} />
          <span className={styles.label} aria-hidden>
            {distanceLabel}
          </span>
        </span>
      ) : null}
    </div>
  );
}
